import React from 'react';

import { DashboardPageLayoutComponent } from 'design/dashboardPageLayout';
import { DashboardContentFallbackLayoutComponent } from 'design/dashboardContentFallbackLayout';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useUserPartnerCode } from 'hooks/useUserPartnerCode';
import { PartnerTrialClientsTable } from 'containers/PartnerClientsTable';
import { CopyPartnerCodeButton } from 'containers/CopyPartnerCodeButton';
import { PartnerClientManager } from 'containers/PartnerClientManager/PartnerClientManager';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { useAppState } from 'hooks/useAppState';
import { usePartnerTrialClients } from 'hooks/usePartnerTrialClients';
import { usePartnerPaidClientTableGroups } from 'hooks/usePartnerPaidClientTableGroups';
import { PartnerPaidClientsTableGroup } from 'containers/PartnerClientsTable/PartnerPaidClientsTableGroup';
import { PartnerClientsTableItem } from 'types/partnerClientsTable';

export const PartnerActiveClientsPage = () => {
  const { partnerClientsPageActive: ownDictionary } = useIntlDictionary();

  const partnerCode = useUserPartnerCode();

  const dispatch = useAppDispatcher();

  const { partnerClientManager } = useAppState();

  const manageClient = React.useCallback(
    (item: PartnerClientsTableItem) => {
      dispatch({
        name: 'PartnerClientsPage__ManageClient',
        clientId: item.id,
        organizationName: item.organization,
      });
    },
    [dispatch]
  );

  const {
    partnerTrialClientsCount,
    partnerTrialLicensesCount,
    partnerTrialClientsTableItems,
  } = usePartnerTrialClients();

  const { partnerPaidClientTableGroups } = usePartnerPaidClientTableGroups();

  return (
    <>
      {partnerTrialClientsTableItems.length > 0 && (
        <DashboardPageLayoutComponent
          content={
            <PartnerTrialClientsTable
              items={partnerTrialClientsTableItems}
              activeItemId={partnerClientManager.clientId}
              onItemClick={manageClient}
            />
          }
          primaryStat={
            <TextComponent size="mp">
              {ownDictionary.trialClientsStatText(
                partnerTrialClientsCount,
                partnerTrialLicensesCount
              )}
            </TextComponent>
          }
          secondaryStat={null}
        />
      )}
      {partnerPaidClientTableGroups.length > 0 &&
        partnerPaidClientTableGroups.map((partnerPaidClientTableGroup) => (
          <DashboardPageLayoutComponent
            key={partnerPaidClientTableGroup.monthDateISO}
            content={
              <PartnerPaidClientsTableGroup
                activeItemId={partnerClientManager.clientId}
                group={partnerPaidClientTableGroup}
                onItemClick={manageClient}
              />
            }
            primaryStat={
              <TextComponent size="mp">
                {ownDictionary.paidClientTableGroupPrimaryStatText(
                  partnerPaidClientTableGroup
                )}
              </TextComponent>
            }
            secondaryStat={
              partnerPaidClientTableGroup.totalIncome > 0 && (
                <TextComponent size="mp">
                  {ownDictionary.paidClientTableGroupSecondaryStatText(
                    partnerPaidClientTableGroup
                  )}
                </TextComponent>
              )
            }
          />
        ))}
      {partnerPaidClientTableGroups.length === 0 &&
        partnerTrialClientsTableItems.length === 0 && (
          <DashboardPageLayoutComponent
            content={
              <DashboardContentFallbackLayoutComponent
                title={
                  <TextComponent size="mp">
                    {ownDictionary.fallbackTitle}
                  </TextComponent>
                }
                description={
                  <TextComponent>
                    {ownDictionary.fallbackDescription}
                  </TextComponent>
                }
                action={<CopyPartnerCodeButton partnerCode={partnerCode} />}
              />
            }
            primaryStat={null}
            secondaryStat={null}
          />
        )}
      <PartnerClientManager />
    </>
  );
};
